import { mediaQueryLarge, requestIdleCallback } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const PRODUCT_CARD_IMAGE_SELECTOR = '.product-media__image';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {WeakMap<HTMLImageElement, string>} */
  #defaultImageSizes = new WeakMap();

  #layoutVersion = 0;
  #syncFrame;
  #gridObserver = new MutationObserver(() => this.#scheduleImageSizes());
  #gridWidth = 0;
  #resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
    const width = entries[0]?.contentRect.width || 0;
    if (width !== this.#gridWidth) { this.#gridWidth = width; this.#scheduleImageSizes(); }
  }) : null;

  #scheduleImageSizes() {
    if (!this.isConnected || this.#syncFrame !== undefined) return;
    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = undefined;
      if (this.isConnected) {
        this.#syncProductCardImageSizes();
        this.refreshPagination?.();
      }
    });
  }

  #ensureStockURL() {
    if (this.dataset?.veyroInStockOnly !== 'true') return true;
    const url = new URL(window.location.href);
    const values = url.searchParams.getAll('filter.v.availability');
    if (values.length === 1 && values[0] === '1') {
      this.removeAttribute('hidden');
      return true;
    }
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.delete('page');
    window.location.replace(url.href);
    return false;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#ensureStockURL()) return;

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#observeGrid();
    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.#ensureStockURL()) return;
    this.#observeGrid();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver.disconnect();
    this.#resizeObserver?.disconnect();
    this.#gridWidth = 0;
    this.#layoutVersion++;
    if (this.#syncFrame !== undefined) cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = undefined;
    this.#defaultImageSizes = new WeakMap();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const version = ++this.#layoutVersion;
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const apply = () => { if (this.isConnected && version === this.#layoutVersion) this.#setLayout(value); };
    // Do not snapshot and animate an increasingly large, auto-loaded grid.
    // Selection feedback stays local to the native density controls.
    apply();
    if (!this.isConnected || version !== this.#layoutVersion) return;
    requestIdleCallback(() => {
      if (!this.isConnected || version !== this.#layoutVersion) return;
      try { sessionStorage.setItem(`product-grid-view-${viewport}`, value); } catch {}
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.refreshPagination?.();
    this.#syncProductCardImageSizes();
  }

  #observeGrid() {
    this.#gridObserver.disconnect();
    this.#resizeObserver?.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver.observe(grid, { childList: true, subtree: true });
    this.#resizeObserver?.observe(grid);
    this.#syncProductCardImageSizes();
  }

  #syncProductCardImageSizes = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const images = /** @type {NodeListOf<HTMLImageElement>} */ (grid.querySelectorAll(PRODUCT_CARD_IMAGE_SELECTOR));

    // Read geometry first, then change responsive-image attributes in one batch.
    const measured = Array.from(images, image => ({
      image,
      width: image.getBoundingClientRect().width || image.closest('.veyro-card-media')?.getBoundingClientRect().width || 0,
    }));
    for (const { image, width } of measured) {
      if (!this.#defaultImageSizes.has(image)) this.#defaultImageSizes.set(image, image.sizes);
      if (width < 1) continue;
      const sizes = `${Math.min(640, Math.ceil(width))}px`;
      if (image.sizes !== sizes) image.sizes = sizes;
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    this.#layoutVersion++;
    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
